import {
  CuttingConfig,
  CuttingConfigLine,
  CreateCuttingConfigRequest,
  UpdateCuttingConfigRequest,
  CuttingConfigFilters,
  CuttingConfigListResponse,
} from '@/types/cutting';

import { api } from '@/api/axios';

// API Response Types (based on backend DTOs)
interface ApiCuttingConfigResponse {
  id: string;
  code: string;
  name: string;
  note: string;
  config_lines_id: string[] | null; // Backend might return null
  createdAt: string;
  updatedAt: string;
}

interface ApiCuttingSizeResponse {
  id: string;
  code: string;
  size: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiCuttingConfigLineResponse {
  id: string;
  code: string;
  cutting_config_id: string;
  cutting_size_id: string;
  cutting_size?: ApiCuttingSizeResponse;
  quantity: number;
  loss_rate: number;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface ApiStandardResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

// Mapping functions
const mapApiCuttingConfigToFrontend = (apiConfig: ApiCuttingConfigResponse): CuttingConfig => ({
  id: apiConfig.id,
  code: apiConfig.code,
  name: apiConfig.name,
  note: apiConfig.note,
  configLinesId: apiConfig.config_lines_id || [], // Handle null/undefined by defaulting to empty array
  createdAt: apiConfig.createdAt,
  updatedAt: apiConfig.updatedAt,
});

const mapApiCuttingConfigLineToFrontend = (apiLine: ApiCuttingConfigLineResponse): CuttingConfigLine => ({
  id: apiLine.id,
  code: apiLine.code,
  cuttingConfigId: apiLine.cutting_config_id,
  cuttingSizeId: apiLine.cutting_size_id,
  cuttingSize: apiLine.cutting_size ? {
    id: apiLine.cutting_size.id,
    code: apiLine.cutting_size.code,
    size: apiLine.cutting_size.size,
    note: apiLine.cutting_size.note,
    createdAt: apiLine.cutting_size.createdAt,
    updatedAt: apiLine.cutting_size.updatedAt,
  } : undefined,
  quantity: apiLine.quantity,
  lossRate: apiLine.loss_rate,
  note: apiLine.note,
  createdAt: apiLine.createdAt,
  updatedAt: apiLine.updatedAt,
});

export const cuttingConfigApi = {
  // Get all cutting configs with pagination and filtering
  getCuttingConfigs: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: CuttingConfigFilters
  ): Promise<CuttingConfigListResponse> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.sortBy) {
        params.append('sort_by', filters.sortBy);
      }
      if (filters?.sortOrder) {
        params.append('sort_order', filters.sortOrder);
      }

      const response = await api.get(`/cutting-configs?${params.toString()}`);
      // The backend returns PaginatedResponse directly, not wrapped in StandardResponse
      const apiResponse: ApiPaginatedResponse<ApiCuttingConfigResponse> = response.data;

      // Handle case where data might be null or undefined
      const data = Array.isArray(apiResponse?.data) ? apiResponse.data : [];

      return {
        data: data.map(mapApiCuttingConfigToFrontend),
        total: apiResponse?.total || 0,
        page: apiResponse?.page || 1,
        pageSize: apiResponse?.pageSize || 10,
      };
    } catch (error: any) {
      console.error('Error fetching cutting configs:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });

      if (error.response?.status === 404) {
        throw new Error('Cutting configs endpoint not found. Please check if the backend server is running.');
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        throw new Error('Cannot connect to backend server. Please check if the server is running.');
      } else {
        throw new Error(
          error.response?.data?.message || error.message || 'Failed to fetch cutting configs'
        );
      }
    }
  },

  // Get single cutting config by ID
  getCuttingConfig: async (id: string): Promise<CuttingConfig> => {
    try {
      const response = await api.get(`/cutting-configs/${id}`);
      // Single item responses are wrapped in StandardResponse
      const apiResponse: ApiStandardResponse<ApiCuttingConfigResponse> = response.data;

      return mapApiCuttingConfigToFrontend(apiResponse.data);
    } catch (error: any) {
      console.error('Error fetching cutting config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting config'
      );
    }
  },

  // Create new cutting config
  createCuttingConfig: async (data: CreateCuttingConfigRequest): Promise<CuttingConfig> => {
    try {
      const payload = {
        name: data.name,
        note: data.note || '',
      };

      const response = await api.post('/cutting-configs', payload);
      const apiResponse: ApiStandardResponse<ApiCuttingConfigResponse> = response.data;
      
      return mapApiCuttingConfigToFrontend(apiResponse.data);
    } catch (error: any) {
      console.error('Error creating cutting config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create cutting config'
      );
    }
  },

  // Update existing cutting config
  updateCuttingConfig: async (id: string, data: UpdateCuttingConfigRequest): Promise<CuttingConfig> => {
    try {
      const payload = {
        name: data.name,
        note: data.note,
      };

      const response = await api.put(`/cutting-configs/${id}`, payload);
      const apiResponse: ApiStandardResponse<ApiCuttingConfigResponse> = response.data;
      
      return mapApiCuttingConfigToFrontend(apiResponse.data);
    } catch (error: any) {
      console.error('Error updating cutting config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update cutting config'
      );
    }
  },

  // Delete cutting config
  deleteCuttingConfig: async (id: string): Promise<void> => {
    try {
      await api.delete(`/cutting-configs/${id}`);
    } catch (error: any) {
      console.error('Error deleting cutting config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete cutting config'
      );
    }
  },

  // Get cutting config lines for a specific config
  getCuttingConfigLines: async (
    cuttingConfigId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<{ data: CuttingConfigLine[]; total: number }> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        cutting_config: cuttingConfigId,
      });

      const response = await api.get(`/cutting-config-lines?${params.toString()}`);
      // Paginated responses are returned directly as PaginatedResponse
      const apiResponse: ApiPaginatedResponse<ApiCuttingConfigLineResponse> = response.data;

      // Handle case where data might be null or undefined
      const data = Array.isArray(apiResponse?.data) ? apiResponse.data : [];

      return {
        data: data.map(mapApiCuttingConfigLineToFrontend),
        total: apiResponse?.total || 0,
      };
    } catch (error: any) {
      console.error('Error fetching cutting config lines:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting config lines'
      );
    }
  },

  // Create multiple cutting config lines at once
  createBulkCuttingConfigLines: async (cuttingConfigId: string, lines: Array<{
    cuttingSizeId: string;
    quantity: number;
    lossRate: number;
    note?: string;
  }>): Promise<CuttingConfigLine[]> => {
    try {
      const payload = lines.map(line => ({
        cutting_config_id: cuttingConfigId,
        cutting_size_id: line.cuttingSizeId,
        quantity: line.quantity,
        loss_rate: line.lossRate,
        note: line.note || '',
      }));

      const response = await api.post('/cutting-config-lines/bulk', payload);
      const apiResponse: ApiStandardResponse<ApiCuttingConfigLineResponse[]> = response.data;

      // Map response to frontend format
      return apiResponse.data.map(mapApiCuttingConfigLineToFrontend);
    } catch (error: any) {
      console.error('Error creating bulk cutting config lines:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create cutting config lines'
      );
    }
  },

  // Update cutting config line
  updateCuttingConfigLine: async (id: string, data: {
    cuttingSizeId?: string;
    quantity?: number;
    lossRate?: number;
    note?: string;
  }): Promise<CuttingConfigLine> => {
    try {
      const payload = {
        cutting_size_id: data.cuttingSizeId,
        quantity: data.quantity,
        loss_rate: data.lossRate,
        note: data.note,
      };

      const response = await api.put(`/cutting-config-lines/${id}`, payload);
      const apiResponse: ApiStandardResponse<ApiCuttingConfigLineResponse> = response.data;
      
      return mapApiCuttingConfigLineToFrontend(apiResponse.data);
    } catch (error: any) {
      console.error('Error updating cutting config line:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update cutting config line'
      );
    }
  },

  // Delete cutting config line
  deleteCuttingConfigLine: async (id: string): Promise<void> => {
    try {
      await api.delete(`/cutting-config-lines/${id}`);
    } catch (error: any) {
      console.error('Error deleting cutting config line:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete cutting config line'
      );
    }
  },
};
